import { useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, Check, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { Finance } from "../lib/api";
import { useConfirm } from "./ConfirmProvider";
import { toast } from "sonner";

const money = (value, currency) => `${Number(value).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

export const UpcomingPayments = ({ data, onReload }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(null);
  const { confirm } = useConfirm();
  if (!data) return null;
  const markPaid = async (item) => {
    if (!(await confirm(`Oznaczyć fakturę ${item.invoice_number} (${money(item.amount, item.currency)}) jako opłaconą?`, { confirmLabel: "Oznacz jako opłaconą" }))) return;
    setBusy(`${item.source}-${item.reference}`);
    try {
      await Finance.setPayment(item.source, item.reference, true);
      toast.success("Faktura oznaczona jako opłacona");
      await onReload();
    } catch (error) { toast.error(error.response?.data?.detail || "Nie zapisano płatności"); }
    finally { setBusy(null); }
  };
  return (
    <section data-testid="upcoming-payments" className="mb-5 border-b-2 border-zinc-200 pb-5 min-w-0">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <BellRing className="w-5 h-5 text-brand shrink-0" />
        <h2 className="font-heading font-bold text-base md:text-lg">Nadchodzące płatności</h2>
        <span data-testid="payment-reminder-count" className="bg-zinc-900 text-white rounded px-2 py-0.5 text-xs font-mono">{data.count}</span>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs ml-auto">
          <span data-testid="payment-overdue-count" className="text-red-700 font-bold">Po terminie: {data.overdue_count}</span>
          <span data-testid="payment-today-count" className="text-amber-800 font-bold">Dzisiaj: {data.today_count}</span>
          <span data-testid="payment-upcoming-count" className="text-zinc-600">W ciągu 3 dni: {data.upcoming_count}</span>
        </div>
      </div>
      {data.count === 0 ? <p data-testid="payments-empty" className="text-sm text-zinc-500 py-3">Brak zaległych i zbliżających się płatności.</p> : (
        <div className="divide-y divide-zinc-200">
          {(expanded ? data.items : data.items.slice(0, 5)).map((item) => {
            const id = `${item.source}-${item.reference}`;
            return <div key={id} data-testid={`payment-reminder-${id}`} className="flex flex-wrap sm:flex-nowrap items-center gap-3 py-3 min-w-0">
              <div className="flex-1 min-w-0 basis-40">
                <Link data-testid={`payment-invoice-${id}`} to={item.source === "ksef" ? "/accounting?tab=purchases" : "/finance"} className="text-sm font-bold hover:text-brand transition-colors break-words">{item.invoice_number}</Link>
                <p data-testid={`payment-seller-${id}`} className="text-xs text-zinc-500 break-words">{item.seller_name}</p>
              </div>
              <div className="text-xs sm:text-right" data-testid={`payment-due-${id}`}>
                <div>{item.due_date}</div>
                <strong className={item.stage === "overdue" ? "text-red-700" : "text-amber-800"}>{item.days_to_due < 0 ? `${-item.days_to_due} dni po terminie` : item.days_to_due === 0 ? "Termin dzisiaj" : `Za ${item.days_to_due} dni`}</strong>
              </div>
              <strong data-testid={`payment-amount-${id}`} className="text-sm font-mono sm:w-36 sm:text-right shrink-0">{money(item.amount, item.currency)}</strong>
              <button data-testid={`payment-mark-paid-${id}`} disabled={!!busy} onClick={() => markPaid(item)} className="inline-flex items-center gap-1.5 border border-zinc-300 rounded px-3 py-2 text-xs font-bold hover:bg-emerald-50 hover:border-emerald-600 transition-colors disabled:opacity-50">
                {busy === id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Oznacz opłaconą
              </button>
            </div>;
          })}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mt-2 text-xs">
        <div data-testid="payment-reminder-totals" className="font-mono font-bold">{Object.entries(data.totals).map(([currency, value]) => money(value, currency)).join(" · ")}</div>
        {data.count > 5 && <button data-testid="payments-expand" onClick={() => setExpanded(!expanded)} className="inline-flex items-center gap-1 hover:text-brand transition-colors">{expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}{expanded ? "Zwiń" : `Wszystkie (${data.count})`}</button>}
      </div>
      {data.missing_due_count > 0 && <p data-testid="payments-missing-due" className="text-xs text-amber-800 mt-2">Bez terminu płatności: {data.missing_due_count}</p>}
    </section>
  );
};